import { useEffect, useRef, useState, useCallback } from 'react';
import { classifyHandGesture } from '../utils/gestureClassifier';

export function useHandTracking({ onSignDetected, videoRef, canvasRef }) {
  const handsRef = useRef(null);
  const cameraRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isTracking, setIsTracking] = useState(false);
  const lastSignRef = useRef(null);
  const signBufferRef = useRef([]);
  const BUFFER_SIZE = 8;

  const drawLandmarks = useCallback((ctx, landmarks, width, height) => {
    if (!landmarks || !ctx) return;

    const connections = [
      [0,1],[1,2],[2,3],[3,4],         // thumb
      [0,5],[5,6],[6,7],[7,8],         // index
      [0,9],[9,10],[10,11],[11,12],    // middle
      [0,13],[13,14],[14,15],[15,16], // ring
      [0,17],[17,18],[18,19],[19,20], // pinky
      [5,9],[9,13],[13,17]            // palm
    ];

    // Draw connections
    ctx.strokeStyle = 'rgba(168, 85, 247, 0.7)';
    ctx.lineWidth = 2;
    connections.forEach(([a, b]) => {
      const pa = landmarks[a], pb = landmarks[b];
      ctx.beginPath();
      ctx.moveTo(pa.x * width, pa.y * height);
      ctx.lineTo(pb.x * width, pb.y * height);
      ctx.stroke();
    });

    // Draw joints
    landmarks.forEach((lm, i) => {
      const x = lm.x * width, y = lm.y * height;
      const isTip = [4, 8, 12, 16, 20].includes(i);
      ctx.beginPath();
      ctx.arc(x, y, isTip ? 6 : 4, 0, Math.PI * 2);
      ctx.fillStyle = isTip ? '#e879f9' : 'rgba(124, 58, 237, 0.9)';
      ctx.fill();
      ctx.strokeStyle = 'white';
      ctx.lineWidth = 1;
      ctx.stroke();
    });
  }, []);

  const onResults = useCallback((results) => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video) return;

    const ctx = canvas.getContext('2d');
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (results.multiHandLandmarks && results.multiHandLandmarks.length > 0) {
      setIsTracking(true);
      const landmarks = results.multiHandLandmarks[0];
      drawLandmarks(ctx, landmarks, canvas.width, canvas.height);

      const gesture = classifyHandGesture(landmarks);

      if (gesture && gesture.confidence > 0.5) {
        // Buffer for stability
        signBufferRef.current.push(gesture.letter);
        if (signBufferRef.current.length > BUFFER_SIZE) {
          signBufferRef.current.shift();
        }

        // Check if sign is stable (majority vote)
        const counts = {};
        signBufferRef.current.forEach(l => { counts[l] = (counts[l] || 0) + 1; });
        const dominant = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];

        if (dominant && dominant[1] >= BUFFER_SIZE * 0.6 && dominant[0] !== lastSignRef.current) {
          lastSignRef.current = dominant[0];
          onSignDetected(gesture);
        }
      } else {
        signBufferRef.current = [];
      }
    } else {
      setIsTracking(false);
      signBufferRef.current = [];
    }
  }, [canvasRef, videoRef, drawLandmarks, onSignDetected]);

  useEffect(() => {
    let hands = null;
    let camera = null;

    const initMediaPipe = async () => {
      try {
        const { Hands } = await import('@mediapipe/hands');
        const { Camera } = await import('@mediapipe/camera_utils');

        hands = new Hands({
          locateFile: (file) =>
            `https://cdn.jsdelivr.net/npm/@mediapipe/hands/${file}`,
        });

        hands.setOptions({
          maxNumHands: 1,
          modelComplexity: 1,
          minDetectionConfidence: 0.7,
          minTrackingConfidence: 0.5,
        });

        hands.onResults(onResults);
        handsRef.current = hands;

        if (videoRef.current) {
          camera = new Camera(videoRef.current, {
            onFrame: async () => {
              if (handsRef.current && videoRef.current) {
                await handsRef.current.send({ image: videoRef.current });
              }
            },
            width: 640,
            height: 480,
          });

          await camera.start();
          cameraRef.current = camera;
          setIsLoading(false);
        }
      } catch (err) {
        console.error('MediaPipe init error:', err);
        setError('Failed to load hand tracking. Please check camera permissions.');
        setIsLoading(false);
      }
    };

    initMediaPipe();

    return () => {
      if (cameraRef.current) {
        try { cameraRef.current.stop(); } catch (e) {}
      }
      if (handsRef.current) {
        try { handsRef.current.close(); } catch (e) {}
      }
    };
  }, [onResults, videoRef]);

  return { isLoading, error, isTracking };
}
